(ns kyber.lib.kyber-db
  "This lib is responsible for managing a flat data structure in the re-frame
  db. It contains all the methods for querying the data strucutre and updating
  it. Note: This state should never be manually modified without usings
  functions in this namespace."
  (:require [re-frame.core :as re-frame]
            [potpuri.core :as pot]
            [kyber.kyber-db.core :refer [db-schema]]))

(defn- clean-record
  "Cleans a record before inserting it into the kyber database"
  [model]
  (if (map? model)
    (->> model
         (remove (fn [[_entity-key entity-value]]
                   (case entity-value
                     ;; Strip out pathom not-found keys
                     :com.wsscode.pathom.core/not-found true
                     false)))
         (into {}))
    model))

(def ^:private db-prefix ::db)
(def ^:private records-key ::records)
(def ^:private sort-key ::sort)

(declare get-records)
(declare tx-records)

(defn schema-relationships
  "This is a WIP: The idea is to give engineers a look into the schema and it's relationships"
  [db-schema*]
  ;; TODO: This has a circular dependency
  (let [clean-schema (dissoc db-schema* :external-user)
        pull-refs (fn pull-refs [refs]
                    (into {} (map (juxt first #(some->> % last :model (get clean-schema) :refs pull-refs))
                                  refs)))]
    (into {} (map (juxt first #(-> % last :refs pull-refs))
                  clean-schema))))

(defn- fmt-child-queries [query]
  (reduce
   (fn [acc prop]
     (if (map? prop)
       (assoc acc (-> prop keys first) (-> prop vals first))
       acc))
   {}
   query))

(defn reference-updater [record model-refs {:keys [many one query]}]
  (let [ref-queries (some-> query fmt-child-queries)]
    (reduce
     (fn [acc [record-ref-key {:keys [model cardinality]}]]
       (let [ident (get acc record-ref-key)]
         (if (some? ident)
           (let [ref-query (get ref-queries record-ref-key)]
             (update acc record-ref-key (case cardinality
                                          :one #(one model % ref-query)
                                          :many #(many model % ref-query)
                                          (js/console.error "Invalid cardinality"))))
           acc)))
     record
     model-refs)))

(defn get-model-schema
  "Returns a model schema."
  [model-key]
  (get-in db-schema [model-key :schema]))

(defn get-model-keys
  "Returns all known keys on a model."
  [model-key]
  (some-> model-key get-model-schema keys vec))

(defn- apply-query-filter
  "Filters a record using the provided query"
  [record query]
  (if (some? query)
    (let [top-level-keys (mapv (fn [key]
                                 (if (map? key)
                                   (first (keys key))
                                   key))
                               query)]
      (select-keys record top-level-keys))
    record))

(defn- append-empty-many-references
  "Appends an empty vector to a model that has references with cardinality many."
  [record refs]
  (let [expected-many-models (->> refs
                                  (filter (fn [[_ {:keys [cardinality]}]] (= :many cardinality)))
                                  (map first))]
    (reduce
     (fn [acc many-key]
       (let [current-value (get acc many-key)]
         (if (and (some? current-value)
                  (seq current-value))
           acc
           (assoc acc many-key []))))
     record
     expected-many-models)))

(defn get-record
  "Pulls a record out of the database"
  ([db model ident]
   (get-record db model ident nil))
  ([db model ident query]
   (let [{:keys [refs]} (model db-schema)
         record (get-in db [db-prefix model records-key ident])]
     (if (and (some? record)
              (some? refs))
       (-> record
           (reference-updater refs {:many #(get-records db %1 %2 %3)
                                    :one #(get-record db %1 %2 %3)
                                    :query query})
           (append-empty-many-references refs)
           (apply-query-filter query))
       (apply-query-filter record query)))))

(defn get-records
  "Pulls a subset of records out of the database"
  ([db model idents]
   (get-records db model idents nil))
  ([db model idents query]
   (->> idents
        (map #(get-record db model % query))
        (remove nil?)
        vec)))

(defn get-sort
  "Pulls a model's sort back"
  [db model]
  (get-in db [db-prefix model ::sort]))

(defn item-exists?
  "Checkes to see if the record is already indexed"
  [db model ident]
  (some? (get-record db model ident)))

(defn- fmt-child-records
  "Groups all child references on a record by their model-key."
  [record refs]
  (reduce
   (fn [acc [record-ref-key {:keys [model cardinality]}]]
     (let [specified-children (get record record-ref-key)]
       (if (some? specified-children)
         ;; If this reference has a cardinality of one, put the
         ;; child into a vec for downstream processing (tx-children)
         ;; which expects it to be a collection of children.
         (update acc model (fn [existing-children]
                             (into (or existing-children [])
                                   (if (= cardinality :one)
                                     [specified-children]
                                     specified-children))))
         acc)))
   {}
   refs))

(defn- tx-children
  "Transacts child records"
  [db children]
  (reduce
   (fn [updated-db [model-key children-records]]
     (if (seq children-records)
       (tx-records updated-db {:model model-key} children-records)
       updated-db))
   db
   children))

(defn- merge-record-children
  "Merges children with an existing record"
  [existing-record new-record refs resort-child-idents]
  (reduce
   (fn [record [child-key child-refs]]
     (let [cardinality (get-in refs [child-key :cardinality])
           existing-children (get record child-key [])]
       (if (= cardinality :many)
         ;; In the case of cardinality many and existing-children refs, join the two reference collections together.
         (assoc record child-key (vec (distinct
                                       ;; When the option :resort-child-idents is set to true, sort order will be updated
                                       ;; upon transaction.
                                       (if resort-child-idents
                                         (into child-refs existing-children)
                                         (into existing-children child-refs)))))
         ;; In the case of cardinality one, always set the new ref value
         (assoc record child-key child-refs))))
   existing-record
   new-record))

(defn- resolve-ident
  [model ident-key record]
  (let [ident-key* (or ident-key (get-in db-schema [model :ident-key]))
        ident (cond
                (keyword? ident-key*) (get record ident-key*)
                (fn? ident-key*) (ident-key* record)
                :else ident-key*)]
    [ident-key* ident]))

;; TODO: If you delete a ref of another model, parents need to be updated to remove the
;;       child from its reference collection.
(defn tx-record
  "Transacts a record into the database"
  [db
   {:keys [model ident-key update-fn resort-child-idents]
    :or {update-fn pot/deep-merge
         resort-child-idents false}}
   raw-record]
  (if (map? raw-record)
    (let [record (clean-record raw-record)
          [ident-key* ident] (resolve-ident model ident-key record)]
      (if (string? ident)
        (let [{:keys [refs]} (model db-schema)
              ;; This is the full record that will be transacted if there is no existing version of it in the db
              new-record (if (some? refs)
                           (reference-updater record
                                              refs
                                              {:many
                                               (fn [model-name references]
                                                 (let [model-ident-key (get-in db-schema [model-name :ident-key])]
                                                   (mapv model-ident-key (or references []))))
                                               :one
                                               (fn [model-name reference]
                                                 (let [model-ident-key (get-in db-schema [model-name :ident-key])]
                                                   (if (fn? model-ident-key)
                                                     (model-ident-key reference)
                                                     (get reference model-ident-key))))})
                           record)

              ;; These are the properties on a model that are not refs
              new-record-properties
              (apply (partial dissoc new-record) (keys refs))

              ;; These are only ref values
              new-record-children
              (select-keys new-record (keys refs))

              ;; Children records
              children (when (some? refs)
                         (fmt-child-records record refs))]
          (cond-> (if (item-exists? db model ident)
                    ;; Record is in the db
                    (-> db
                        (update-in [db-prefix model records-key ident] update-fn new-record-properties)
                        (update-in [db-prefix model records-key ident] merge-record-children new-record-children refs resort-child-idents))
                    ;; Record is not in the db
                    (-> db
                        (assoc-in [db-prefix model records-key ident] new-record)
                        (update-in [db-prefix model sort-key] #(if (some? %)
                                                                 (conj % ident)
                                                                 [ident]))))
            (some? children)
            (tx-children children)))
        (do
          (js/console.error "Failed to transact record (Invalid Ident)"
                            {:ident ident
                             :ident-key ident-key*
                             :record record})
          db)))
    (do
      (js/console.error "Failed to transact record (Invalid Type)"
                        {:record raw-record})
      db)))

(defn tx-records
  "Transacts multiple records of an identical model"
  [db opts records]
  (if (vector? (if (list? records) (vec records) records))
    (reduce #(tx-record %1 opts %2) db records)
    (do
      (js/console.error "Failed to transact records (Invalid Type)"
                        {:records records})
      db)))

(defn retract-record
  "Removes a record from the db"
  [db {:keys [model ident-key]} record]
  (let [[ident-key* ident] (resolve-ident model ident-key record)]
    (if (string? ident)
      (let [{:keys [_refs]} (model db-schema)]
        (if (item-exists? db model ident)
          (-> db
              (update-in [db-prefix model records-key] dissoc ident)
              (update-in [db-prefix model sort-key] #(vec (remove (fn [i] (= i ident)) %))))
          db))
      (js/console.error "Failed to retract record (Invalid Ident)"
                        {:ident ident
                         :ident-key ident-key*
                         :record record}))))

(defn retract-records
  "Removes multiple records from the db of an identical model"
  [db opts records]
  (reduce #(retract-record %1 opts %2) db records))

(defn retract-ref
  "Removes a ref to an existing record"
  [db {:keys [model ident-key refs-key ref-ident-key]} record]
  (let [[ident-key* ident] (resolve-ident model ident-key record)]
    (if (string? ident)
      (if (item-exists? db model ident)
        (update-in db [db-prefix model records-key ident refs-key]
                   #(vec (remove (fn [r] (= r ref-ident-key)) %)))
        db)
      (js/console.error "Failed to retract ref (Invalid Ident)"
                        {:ident ident
                         :ident-key ident-key*
                         :record record}))))

(defn retract-refs
  "Removes refs to existing records of an identical model"
  [db {:keys [model ident-key refs-key ref-ident-keys all?]} record]
  (let [[ident-key* ident] (resolve-ident model ident-key record)
        should-remove-ref? (if all?
                             #(constantly true)
                             (partial contains? (set ref-ident-keys)))]
    (if (string? ident)
      (if (item-exists? db model ident)
        (update-in db [db-prefix model records-key ident refs-key]
                   #(vec (remove should-remove-ref? %)))
        db)
      (js/console.error "Failed to retract refs (Invalid Ident)"
                        {:ident ident
                         :ident-key ident-key*
                         :record record}))))

(defn- get-record-sub
  "Subscription that pull a single records out of the db"
  [db [_ {:keys [model ident query]}]]
  (get-record db model ident query))

(defn- get-records-sub
  "Subscription that pull many records out of the db with an identical model"
  [db [_ {:keys [model idents query]}]]
  (get-records db model idents query))

(defn- get-model-records-sub
  "Subscription that pull all records out of the db by model"
  [db [_ {:keys [model query]}]]
  (get-records db model (get-sort db model) query))

(defn- get-sort-sub
  "Subscription that pulls the sort of a model back."
  [db [_ {:keys [model]}]]
  (get-sort db model))

(defn register-subscriptions
  []
  (re-frame/reg-sub ::get-sort get-sort-sub)
  (re-frame/reg-sub ::get-model-records get-model-records-sub)
  (re-frame/reg-sub ::get-record get-record-sub)
  (re-frame/reg-sub ::get-records get-records-sub))

(defn- retract-ref-event
  "Event that retracts a ref to an existing record"
  [db [_ props record]]
  (retract-ref db props record))

(defn register-events
  []
  (re-frame/reg-event-db ::retract-ref retract-ref-event))
